import type { Connector } from '@eon/types';
import { useTranslation } from '@eon/i18n';
import { NumberField, SelectField } from '@eon/ui';

export interface ConnectorSelectProps {
  connectors: Connector[];
  value: number | '';
  onChange: (value: number | '') => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  allowZero?: boolean;
  placeholder?: string;
}

export function ConnectorSelect({
  connectors,
  value,
  onChange,
  disabled,
  size = 'md',
  allowZero = false,
  placeholder,
}: ConnectorSelectProps) {
  const { t } = useTranslation();

  if (connectors.length === 0) {
    return (
      <NumberField
        value={value}
        onChange={onChange}
        min={allowZero ? 0 : 1}
        disabled={disabled}
      />
    );
  }

  const connectorStatus = (status: string | undefined) => {
    if (!status) return '—';
    const key = `device.status.${status}` as const;
    const translated = t(key);
    return translated === key ? status : translated;
  };

  const options = connectors.map((c) => ({
    value: String(c.id),
    label: t('deviceControl.connector.option', {
      id: c.id,
      status: connectorStatus(c.status),
    }),
  }));

  if (allowZero) {
    options.unshift({
      value: '0',
      label: t('deviceControl.connector.all'),
    });
  }

  const strValue = value === '' ? '' : String(value);

  return (
    <SelectField
      value={strValue || undefined}
      onValueChange={(v) => onChange(v === '' ? '' : Number(v))}
      disabled={disabled}
      size={size}
      placeholder={placeholder ?? t('deviceControl.connector.placeholder')}
      options={options}
    />
  );
}
